// knowledgebase-agent.service.js
import fetchWithAuthRetry from "./fetchWithAuthRetry";
import { showMessage } from "../customhooks/useNotifier";

import environment from "../environments/environment";
const API_URL = environment.REACT_APP_BACKEND_API_BASE_URL;

const knowledgebaseAgentService = {
  // Create a Knowledge Base
  createKnowledgeBase: async (data) => {
    try {
      const response = await fetchWithAuthRetry(
        `${API_URL}/api/v1/knowledgebase-agent/create`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        },
      );

      const result = await response.json();

      // Handle non-OK responses
      if (!response.ok) {
        // Extract safest error message
        const errorMsg =
          result?.message?.message || // nested message
          result?.message || // direct message
          "Knowledge Base creation failed";

        throw new Error(errorMsg);
      }

      showMessage("✅ Knowledge Base created successfully", "success");
      return result;
    } catch (error) {
      console.error("❌ Create Knowledge Base Error:", error);

      const friendlyMessage =
        error?.message || "Failed to create knowledge base";

      showMessage(friendlyMessage, "error");
      throw error;
    }
  },

  // Update KB
  updateKnowledgeBase: async (kbId, data) => {
    try {
      const response = await fetchWithAuthRetry(
        `${API_URL}/api/v1/knowledgebase-agent/update/${kbId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        },
      );

      const result = await response.json();
      if (!response.ok)
        throw new Error(result?.message || "Knowledge Base update failed");

      showMessage("Knowledge Base updated successfully", "success");
      return result;
    } catch (error) {
      console.error("❌ Update Knowledge Base Error:", error);
      showMessage(error.message || "Failed to update knowledge base", "error");
      throw error;
    }
  },

  // Delete KB
  deleteKnowledgeBase: async (kbId) => {
    try {
      const response = await fetchWithAuthRetry(
        `${API_URL}/api/v1/knowledgebase-agent/delete/${kbId}`,
        { method: "DELETE" },
      );

      const result = await response.json();
      if (!response.ok)
        throw new Error(result?.message || "Knowledge Base deletion failed");

      showMessage("Knowledge Base deleted successfully.", "success");
      return result;
    } catch (error) {
      console.error("❌ Delete Knowledge Base Error:", error);
      showMessage(error.message || "Failed to delete knowledge base", "error");
      throw error;
    }
  },

  // Get KB by ID
  getKnowledgeBaseById: async (kbId) => {
    try {
      const response = await fetchWithAuthRetry(
        `${API_URL}/api/v1/knowledgebase-agent/${kbId}`,
        { method: "GET" },
      );

      const result = await response.json();
      if (!response.ok)
        throw new Error(result?.message || "Failed to fetch knowledge base");

      return result;
    } catch (error) {
      console.error("❌ Get Knowledge Base by ID Error:", error);
      showMessage(error.message || "Error loading knowledge base", "error");
      throw error;
    }
  },

  // Get all KBs
  getAllKnowledgeBases: async () => {
    try {
      const response = await fetchWithAuthRetry(
        `${API_URL}/api/v1/knowledgebase-agent`,
        { method: "GET" },
      );

      const result = await response.json();
      if (!response.ok)
        throw new Error(result?.message || "Failed to fetch knowledge bases");

      return result;
    } catch (error) {
      console.error("❌ Get All Knowledge Bases Error:", error);
      showMessage(error.message || "Failed to load knowledge bases", "error");
      throw error;
    }
  },

  // Upload documents
  uploadDocuments: async (formDataUpload) => {
    try {
      const response = await fetchWithAuthRetry(
        `${API_URL}/api/v1/knowledgebase-agent/uploaddocuments`,
        {
          method: "POST",
          body: formDataUpload,
        },
      );

      const result = await response.json();
      if (!response.ok)
        throw new Error(result?.message || "Document upload failed");

      showMessage("📄 Documents uploaded successfully", "success");
      return result;
    } catch (error) {
      console.error("❌ Document Upload Error:", error);
      showMessage(error.message || "Failed to upload documents", "error");
      throw error;
    }
  },

  // Start website crawling
  startCrawling: async (kbId, websiteUrl) => {
    try {
      const response = await fetchWithAuthRetry(
        `${API_URL}/api/v1/knowledgebase-agent/crawlwebsite`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ kbId, websiteUrl }),
        },
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.message || "Failed to start crawling");
      }

      showMessage(
        result.message || "🚀 Crawling started successfully",
        "success",
      );
      return result;
    } catch (error) {
      console.error("❌ Start Crawling Error:", error);
      showMessage(error.message || "Failed to start crawling", "error");
      throw error;
    }
  },

  // Chat Agent Page services
  fetchPublicAgentById: async (id) => {
    try {
      const response = await fetch(
        `${API_URL}/api/v1/knowledgebase-agent/check-public-agent-kb`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ id }),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch public agent widget");
      }

      return data;
    } catch (error) {
      showMessage(error.message || "Network error", "error");
      throw error;
    }
  },
  fetchPublicAgentByIdAndUrl: async (payload) => {
    try {
      console.log("payload-fetchPublicAgentByIdAndUrl--->", payload);

      const response = await fetch(
        `${API_URL}/api/v1/knowledgebase-agent/check-public-agent-kb-id-url`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch public agent widget");
      }

      return data;
    } catch (error) {
      showMessage(error.message || "Network error", "error");
      throw error;
    }
  },
  fetchSitemapLinks: async (websiteUrl) => {
    const response = await fetchWithAuthRetry(
      `${API_URL}/api/v1/knowledgebase-agent/fetch-sitemap-links`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ websiteUrl }),
      },
    );

    const result = await response.json();

    if (!response.ok) {
      let errorMessage = "Failed to fetch sitemap";

      // ✔ Case 1: message is string
      if (typeof result?.message === "string") {
        errorMessage = result.message;

        // ✔ Case 2: message is nested object (YOUR CASE)
      } else if (typeof result?.message?.message === "string") {
        errorMessage = result.message.message;

        // ✔ Case 3: fallback
      } else if (typeof result?.error === "string") {
        errorMessage = result.error;
      }

      throw new Error(errorMessage);
    }

    return result;
  },
  // Store selected sitemap links (no crawling)
  storeSitemapLinks: async (kbId, urls) => {
    try {
      if (!kbId || !Array.isArray(urls) || urls.length === 0) {
        throw new Error("kbId and urls are required");
      }

      const response = await fetchWithAuthRetry(
        `${API_URL}/api/v1/knowledgebase-agent/sitemap-store-links`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ kbId, urls }),
        },
      );

      const result = await response.json();

      if (!response.ok) {
        const errorMsg =
          result?.message?.message ||
          result?.message ||
          "Failed to store sitemap links";

        throw new Error(errorMsg);
      }

      // showMessage(
      //   result?.message || "🔗 Sitemap links stored successfully",
      //   "success"
      // );

      return result;
    } catch (error) {
      console.error("❌ Store Sitemap Links Error:", error);
      showMessage(error?.message || "Failed to store sitemap links", "error");
      throw error;
    }
  },
};

export default knowledgebaseAgentService;
